import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import usePageMetadata from '../hooks/usePageMetadata';
import * as ordersApi from '../api/endpoints/orders';
import { btnMd, btnPrimary, containerNarrow, focusRing, pageHeading, pageSection } from '../styles/ui';

// Sale.paymentStatus / Sale.status enums (see backend Sale model) collapsed
// into the four outcomes this page actually needs to distinguish. A
// cancelled order only counts as "refunded" once money that was actually
// taken has actually come back — a cancelled COD order (never charged) or
// one still mid-refund (refund_pending) must not tell the customer they
// were already refunded.
const getOutcome = (order) => {
  if (order.paymentStatus === 'refunded') return 'refunded';
  if (order.status === 'cancelled' || order.paymentStatus === 'refund_pending') return 'cancelled';
  if (order.paymentStatus === 'processing' || order.paymentStatus === 'partial') return 'pending';
  return 'placed';
};

const OrderConfirmationPage = () => {
  const { saleId } = useParams();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const requestIdRef = useRef(0);
  const invalidateRequests = useCallback(() => {
    ++requestIdRef.current;
  }, []);

  usePageMetadata({
    title: 'Order Confirmed | Fresh & Homely',
    description: 'Your Fresh & Homely order has been placed.',
    canonicalPath: `/order-confirmation/${saleId}`,
  });

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setState({ status: 'loading', data: null, error: null });
    ordersApi
      .getOrder(saleId)
      .then((response) => {
        if (requestId === requestIdRef.current) {
          setState({ status: 'success', data: response.data, error: null });
        }
      })
      .catch((err) => {
        if (requestId === requestIdRef.current) {
          setState({ status: 'error', data: null, error: err.message });
        }
      });
  }, [saleId]);

  useEffect(() => {
    load();
    return invalidateRequests;
  }, [load, invalidateRequests]);

  // Processing payments and refunds both need silent polling. Refunded and
  // other terminal states stop polling, while refund_pending keeps the
  // cancellation/refund-processing copy visible between requests.
  useEffect(() => {
    const paymentStatus = state.status === 'success' ? state.data.paymentStatus : null;
    const shouldPoll = state.status === 'success' && (
      getOutcome(state.data) === 'pending' || paymentStatus === 'refund_pending'
    );
    if (!shouldPoll) return undefined;

    let cancelled = false;
    let timeoutId;
    const poll = async () => {
      const requestId = ++requestIdRef.current;
      try {
        const response = await ordersApi.getOrder(saleId);
        if (!cancelled && requestId === requestIdRef.current) {
          setState({ status: 'success', data: response.data, error: null });
        }
      } catch {
        // Transient poll failure — keep the current pending view and retry.
      } finally {
        if (!cancelled) timeoutId = setTimeout(poll, 5000);
      }
    };

    timeoutId = setTimeout(poll, 5000);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      invalidateRequests();
    };
  }, [saleId, state.status, state.data, invalidateRequests]);

  return (
    <>
      <Navbar />
      <section className={pageSection}>
        <div className={`${containerNarrow} text-center`}>
          {state.status === 'loading' && <LoadingState label="Loading order..." />}
          {state.status === 'error' && <ErrorState message={state.error} onRetry={load} />}
          {state.status === 'success' && (() => {
            const outcome = getOutcome(state.data);
            const invoiceSuffix = state.data.invoiceNumber ? ` — invoice ${state.data.invoiceNumber}` : '';
            return (
              <>
                {outcome === 'placed' && (
                  <div className="w-20 h-20 bg-success-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-glass">
                    <CheckCircle2 className="text-success-600" size={36} aria-hidden="true" />
                  </div>
                )}
                {outcome === 'pending' && (
                  <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-glass">
                    <Clock className="text-amber-600" size={36} aria-hidden="true" />
                  </div>
                )}
                {(outcome === 'refunded' || outcome === 'cancelled') && (
                  <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-glass">
                    <XCircle className="text-red-600" size={36} aria-hidden="true" />
                  </div>
                )}

                <h1 className={`${pageHeading} mb-3`}>
                  {outcome === 'placed' && 'Order Placed!'}
                  {outcome === 'pending' && 'Payment Processing'}
                  {outcome === 'refunded' && 'Order Refunded'}
                  {outcome === 'cancelled' && 'Order Cancelled'}
                </h1>
                <p className="text-ink-secondary mb-8">
                  {outcome === 'placed' &&
                    `Thank you for your order${invoiceSuffix}. We'll get it ready for you soon.`}
                  {outcome === 'pending' &&
                    `We're still confirming your payment${invoiceSuffix}. This page will update once it clears — check your order history for the latest status.`}
                  {outcome === 'refunded' &&
                    `Your payment was refunded${invoiceSuffix}. Please check your order history for details.`}
                  {outcome === 'cancelled' &&
                    (state.data.paymentStatus === 'refund_pending'
                      ? `This order was cancelled${invoiceSuffix}. Your refund is being processed and will reflect soon.`
                      : `This order was cancelled${invoiceSuffix}.`)}
                </p>
                <div className="flex items-center justify-center gap-4">
                  <Link
                    to={`/orders/${saleId}`}
                    className={`${btnPrimary} ${btnMd}`}
                  >
                    View Order
                  </Link>
                  <Link to="/categories" className={`text-emerald-700 hover:text-emerald-800 font-medium ${focusRing}`}>
                    Continue Shopping
                  </Link>
                </div>
              </>
            );
          })()}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default OrderConfirmationPage;
